import React, { useState } from "react";
import { Box, ButtonBase, Dialog, DialogContent, DialogTitle, Tooltip, Typography } from "@mui/material";
import {
  CropSquare,
  DashboardCustomize,
  TableRestaurant,
  TableBar,
  TextFields,
  RadioButtonUnchecked,
  HorizontalRule,
  VisibilityOff,
  Keyboard,
  Gesture,
} from "@mui/icons-material";
import useEditorMapa from "../../stores/useEditorMapa";
import { agregarSector, crearSectorGeneral, crearDecorativo, agregarDecorativo, conLugares } from "../../domain/mapa/mapa";
import { crearSectorFilas, crearSectorMesas, agregarMesa } from "../../domain/mapa/generador";
import { cambiarSector } from "./acciones";
import { COLOR, ANCHO_PANEL_IZQ } from "./estilos";

// Opcion: ícono y una línea; la ayuda va en el tooltip para no recargar la lista
const Opcion = ({ icono, texto, onClick, deshabilitada, ayuda }) => (
  <Tooltip title={ayuda || ""} placement="right" disableInteractive>
    <span>
      <ButtonBase
        onClick={onClick}
        disabled={deshabilitada}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          gap: 1.25,
          px: 1.25,
          py: 0.9,
          borderRadius: "8px",
          color: deshabilitada ? COLOR.textoSuave : COLOR.texto,
          opacity: deshabilitada ? 0.6 : 1,
          "&:hover": { bgcolor: "#f4f4f5", color: COLOR.titulo },
          "& svg": { fontSize: 19, color: COLOR.textoSuave },
          "&:hover svg": { color: COLOR.acento },
        }}
      >
        {icono}
        <Typography sx={{ fontSize: 14 }}>{texto}</Typography>
      </ButtonBase>
    </span>
  </Tooltip>
);

const Titulo = ({ children }) => (
  <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, color: COLOR.textoSuave, px: 1.25, pt: 2.5, pb: 0.75 }}>
    {children}
  </Typography>
);

const ATAJOS = [
  ["Deshacer", "Ctrl + Z"],
  ["Rehacer", "Ctrl + Shift + Z  /  Ctrl + Y"],
  ["Duplicar", "Ctrl + D"],
  ["Borrar", "Supr / Retroceso"],
  ["Mover de a poco", "Flechas (con Shift, de a 10)"],
  ["Quitar la selección", "Esc"],
  ["Herramienta de selección", "V"],
  ["Mover el plano", "H o mantener la barra espaciadora"],
  ["Zoom", "Ctrl + rueda del mouse o pellizcar"],
];

const PanelAgregar = ({ lienzoRef }) => {
  const cambiar = useEditorMapa((s) => s.cambiar);
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const [verAtajos, setVerAtajos] = useState(false);

  const centro = () => lienzoRef.current?.centroVisible() || { x: 100, y: 100 };

  const nuevoSector = (enMapa) => {
    const { x, y } = centro();
    let clave = null;
    cambiar((m) => {
      const sector = crearSectorGeneral(m, { x: x - 100, y: y - 60, enMapa });
      clave = sector.clave;
      return agregarSector(m, sector);
    });
    seleccionar({ tipo: "sector", clave });
  };

  const nuevasFilas = (config) => {
    const { x, y } = centro();
    let clave = null;
    cambiar((m) => {
      const sector = crearSectorFilas(m, { x: x - 150, y: y - 80 }, config);
      clave = sector.clave;
      return agregarSector(m, sector);
    });
    seleccionar({ tipo: "sector", clave });
  };

  // la mesa va al sector con lugares que esté elegido; si no hay, a uno nuevo
  const nuevaMesa = (forma) => {
    const { seleccion, historial } = useEditorMapa.getState();
    const claveSector = seleccion?.tipo === "mesa" ? seleccion.sector : seleccion?.tipo === "sector" ? seleccion.clave : null;
    const destino = historial.presente.sectores.find((s) => s.clave === claveSector);
    if (destino && conLugares(destino)) {
      const r = cambiarSector(cambiar, destino.clave, (s) => agregarMesa(s, { forma, sillas: forma === "rectangular" ? 8 : 6 }));
      if (r?.clave) seleccionar({ tipo: "mesa", sector: destino.clave, clave: r.clave });
      return;
    }
    const { x, y } = centro();
    let clave = null;
    cambiar((m) => {
      const sector = crearSectorMesas(m, { x: x - 60, y: y - 60 }, forma);
      clave = sector.clave;
      return agregarSector(m, sector);
    });
    seleccionar({ tipo: "sector", clave });
  };

  const nuevoObjeto = (tipo) => {
    const { x, y } = centro();
    const decorativo = crearDecorativo(tipo, { x: x - 40, y: y - 20 });
    cambiar((m) => agregarDecorativo(m, decorativo));
    seleccionar({ tipo: "decorativo", clave: decorativo.clave });
  };

  return (
    <Box
      sx={{
        width: ANCHO_PANEL_IZQ,
        flexShrink: 0,
        borderRight: `1px solid ${COLOR.borde}`,
        bgcolor: COLOR.panel,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      }}
    >
      <Box sx={{ px: 1, flex: 1 }}>
        <Titulo>UBICACIONES</Titulo>
        <Opcion icono={<CropSquare fontSize="small" />} texto="Ubicación general" ayuda="Se vende por cantidad: campo, pista, general" onClick={() => nuevoSector(true)} />
        <Opcion
          icono={<VisibilityOff fontSize="small" />}
          texto="Fuera del mapa"
          ayuda="No se dibuja: prensa, invitados, staff"
          onClick={() => nuevoSector(false)}
        />

        <Titulo>ASIENTOS Y MESAS</Titulo>
        <Opcion icono={<DashboardCustomize fontSize="small" />} texto="Filas de asientos" ayuda="Platea con filas numeradas" onClick={() => nuevasFilas({ curva: 0 })} />
        <Opcion
          icono={<Gesture fontSize="small" />}
          texto="Filas curvas"
          ayuda="Anfiteatro o gradas en arco"
          onClick={() => nuevasFilas({ curva: 45, filas: 4, asientosPorFila: 12 })}
        />
        <Opcion
          icono={<TableRestaurant fontSize="small" />}
          texto="Mesa redonda"
          ayuda="Se suma a la ubicación elegida, o crea una nueva"
          onClick={() => nuevaMesa("redonda")}
        />
        <Opcion
          icono={<TableBar fontSize="small" />}
          texto="Mesa rectangular"
          ayuda="Se suma a la ubicación elegida, o crea una nueva"
          onClick={() => nuevaMesa("rectangular")}
        />

        <Titulo>OBJETOS</Titulo>
        <Opcion icono={<TextFields fontSize="small" />} texto="Texto" onClick={() => nuevoObjeto("texto")} />
        <Opcion icono={<CropSquare fontSize="small" />} texto="Rectángulo" ayuda="Escenario, barra, pasillo" onClick={() => nuevoObjeto("rectangulo")} />
        <Opcion icono={<RadioButtonUnchecked fontSize="small" />} texto="Círculo" onClick={() => nuevoObjeto("circulo")} />
        <Opcion icono={<HorizontalRule fontSize="small" />} texto="Línea" onClick={() => nuevoObjeto("linea")} />
      </Box>

      <Box sx={{ borderTop: `1px solid ${COLOR.borde}`, p: 1 }}>
        <Opcion icono={<Keyboard fontSize="small" />} texto="Atajos de teclado" onClick={() => setVerAtajos(true)} />
      </Box>

      <Dialog open={verAtajos} onClose={() => setVerAtajos(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: COLOR.titulo }}>Atajos de teclado</DialogTitle>
        <DialogContent>
          {ATAJOS.map(([accion, teclas]) => (
            <Box key={accion} sx={{ display: "flex", justifyContent: "space-between", py: 0.75, gap: 2 }}>
              <Typography sx={{ fontSize: 14 }}>{accion}</Typography>
              <Typography sx={{ fontSize: 13, color: COLOR.textoSuave, textAlign: "right" }}>{teclas}</Typography>
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PanelAgregar;
